import { parse } from 'qs';
import type { FC } from 'react';
import { Navigate, useLocation } from 'react-router';
import type { TransactionType } from 'src/apis/capacity-management/types';
import { PageSpinner } from 'src/components/spinner-container';
import { stringifySearchParams } from 'src/hooks/use-page-params-from-schema';
import {
  defaultTransportTabPath,
  getAvailableTransportTabs,
  transportTabPathname,
  type TransportTabAvailability,
  type TransportTabPath,
} from 'src/pages/company-transactions/transport/tabs';
import { useTransportTabAvailability } from 'src/pages/company-transactions/transport/use-transport-tab-availability';
import { useCompanyTransactionsAccess } from 'src/pages/company-transactions/use-company-transactions-access';
import { ReportingTransactions } from 'src/pages/reporting/my-transactions';

/**
 * The Transport tab of every transaction type, with the tab params that show
 * only that type, e.g. `RESHUFFLE_FROM` → Reshuffling with `side=FROM`.
 *
 * A new `TransactionType` fails the build until it gets an entry here. The tab
 * pages list their own types, so keep them in sync with this table.
 */
const transactionTypeTabs = {
  AUCTION: { tab: 'auctions' },
  DIRECT_FCFS: { tab: 'fcfs' },
  FCFS: { tab: 'fcfs' },
  ASSIGNMENT_BUY: {
    tab: 'secondary',
    tabParams: { side: 'BUY', kind: 'ASSIGNMENT' },
  },
  TRANSFER_OF_USE_BUY: {
    tab: 'secondary',
    tabParams: { side: 'BUY', kind: 'TRANSFER_OF_USE' },
  },
  ASSIGNMENT_SELL: {
    tab: 'secondary',
    tabParams: { side: 'SELL', kind: 'ASSIGNMENT' },
  },
  TRANSFER_OF_USE_SELL: {
    tab: 'secondary',
    tabParams: { side: 'SELL', kind: 'TRANSFER_OF_USE' },
  },
  CONVERSION: { tab: 'conversions' },
  MULTI_YEAR_TRANSPORT_ALLOCATION: { tab: 'multi-year' },
  RESHUFFLE_FROM: { tab: 'reshuffling', tabParams: { side: 'FROM' } },
  RESHUFFLE: { tab: 'reshuffling', tabParams: { side: 'TO' } },
  SURRENDER: { tab: 'surrender' },
} as const satisfies Record<
  TransactionType,
  { tab: TransportTabPath; tabParams?: Record<string, string> }
>;

type Tab = { tab: TransportTabPath; tabParams: Record<string, string> };

const defaultTab: Tab = { tab: defaultTransportTabPath, tabParams: {} };

/**
 * The Transport tab for the transaction types of a My Transactions link:
 * - one type → its tab with its tab params,
 *   e.g. `ASSIGNMENT_BUY` → Secondary with `side=BUY&kind=ASSIGNMENT`
 * - several types of one tab → that tab without params,
 *   e.g. `ASSIGNMENT_BUY`, `ASSIGNMENT_SELL` → Secondary
 * - no types, types of several tabs, or a tab that is not available → the
 *   default tab, e.g. `AUCTION`, `FCFS` → Auction Allocations
 *
 * Values that are not transaction types are skipped.
 */
function getTab(
  transactionTypes: readonly unknown[],
  availability: TransportTabAvailability
): Tab {
  const [type, ...otherTypes] = [...new Set(transactionTypes)].filter(
    (value): value is TransactionType =>
      typeof value === 'string' && Object.hasOwn(transactionTypeTabs, value)
  );
  if (!type) return defaultTab;

  const typeTab = transactionTypeTabs[type];
  const isOneTab = otherTypes.every(
    (otherType) => transactionTypeTabs[otherType].tab === typeTab.tab
  );
  const isAvailable = getAvailableTransportTabs(availability).some(
    (tab) => tab.path === typeTab.tab
  );
  if (!isOneTab || !isAvailable) return defaultTab;

  return {
    tab: typeTab.tab,
    tabParams:
      otherTypes.length === 0 && 'tabParams' in typeTab
        ? typeTab.tabParams
        : {},
  };
}

/**
 * Turns a My Transactions URL search into a Company Transactions location:
 * - `transactionType` picks the Transport tab and its tab params,
 * - `start` is dropped, so the tab opens on its first page,
 * - every other param (filters, sorting) is copied as it is.
 *
 * E.g. `?transactionType=ASSIGNMENT_BUY&sortDirection=asc&start=20` opens
 * Secondary with `?sortDirection=asc&side=BUY&kind=ASSIGNMENT`.
 *
 * The search is parsed as is, not with the My Transactions page params, so old
 * links keep working after that page is removed.
 */
export function getCompanyTransactionsLocation(
  search: string,
  availability: TransportTabAvailability
) {
  // `start` is taken out only to leave it out of `params`: the tab opens on
  // its first page.
  const { transactionType, start, ...params } = parse(search.substring(1), {
    arrayLimit: Infinity,
  });
  const { tab, tabParams } = getTab([transactionType].flat(), availability);

  return {
    pathname: transportTabPathname(tab),
    search: stringifySearchParams({ ...params, ...tabParams }),
  };
}

export const MyTransactionsRedirect: FC = () => {
  const access = useCompanyTransactionsAccess();

  if (!access.loaded) return <PageSpinner />;

  if (!access.canAccess) return <ReportingTransactions />;

  return <CompanyTransactionsRedirect />;
};

const CompanyTransactionsRedirect = () => {
  const { search, state } = useLocation();
  const availability = useTransportTabAvailability();

  if (!availability.loaded) return <PageSpinner />;

  return (
    <Navigate
      replace
      to={getCompanyTransactionsLocation(search, availability.value)}
      state={state}
    />
  );
};
